<template>
  <Sheet>
    <template #leading>
      <Sheet.Button :icon="CloseIcon" @click="close()" />
    </template>
    <template #title>Select registered work</template>
    <template #trailing>
      <Sheet.Button
        variant="primary"
        :icon="CheckmarkIcon"
        :disabled="!selected"
        @click="confirm()"
      />
    </template>

    <div class="shrink-0 px-4 pt-2.5 pb-4">
      <Breadcrumb
        :key="breadcrumbKey"
        :items="breadcrumbItems"
        collapse
        @select="handleBreadcrumbSelect"
      />
    </div>

    <div class="pl-4 min-h-0 flex flex-col">
      <Table
        selection="single"
        :sticky-header="false"
        v-model:selected="selectedIds"
        v-model:sort="sort"
        :row-ids="fileRowIds"
        class="flex min-h-0 flex-col"
      >
        <Table.Head>
          <Table.Header primary sortable field="name">Name</Table.Header>
          <Table.Header class="w-[250px] shrink-0" sortable field="timestamp">
            Dacr Timestamp
          </Table.Header>
        </Table.Head>

        <div
          class="overflow-y-auto flex-1 min-h-0 overscroll-contain"
          @scroll="handleScroll"
        >
          <div
            v-if="loading && !items.length"
            class="flex h-[160px] items-center justify-center"
          >
            <Spinner :size="22" />
          </div>

          <template v-else>
            <Table.Row
              v-for="item in items"
              :id="item.id"
              :key="item.id"
              @row-click="handleRowClick(item)"
            >
              <template #media>
                <div class="w-9 tablet-h:w-6">
                  <Thumbnail
                    v-if="isFile(item)"
                    :type="fileTypeToThumbnail(item.type)"
                    :src="thumbSrc(item)"
                  />
                  <Thumbnail v-else type="folder" />
                </div>
              </template>

              <Table.Cell primary>
                <span class="truncate">{{ item.name }}</span>
              </Table.Cell>

              <template #mobile>
                <div class="flex w-full min-w-0 items-center pl-[10px]">
                  <div v-truncate class="w-full">
                    <p
                      data-truncate-target
                      class="truncate text-[14px] leading-[18px] text-gray-800"
                    >
                      {{ item.name }}
                    </p>
                    <div
                      v-if="isFile(item)"
                      class="mt-[2px] flex items-center gap-[6px] text-[13px] leading-[18px] text-gray-600"
                    >
                      <DacrMarkIcon
                        class="size-[14px] shrink-0 text-gray-600"
                      />
                      <span class="truncate">{{ timestampLabel(item) }}</span>
                      <span
                        v-if="moreCount(item)"
                        class="flex shrink-0 items-center gap-x-1 text-[12px] font-medium text-gray-500"
                      >
                        +{{ moreCount(item) }} more
                        <ChevronUpIcon class="size-[10px] rotate-180" />
                      </span>
                    </div>
                  </div>
                </div>
              </template>
            </Table.Row>

            <div
              v-if="!items.length"
              class="px-4 py-10 text-center text-[14px] text-gray-500"
            >
              No registered works here
            </div>
          </template>
        </div>
      </Table>
    </div>
  </Sheet>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Sheet, Table, Thumbnail, Breadcrumb, useSheet } from '@dacr/library'
import {
  CloseIcon,
  CheckmarkIcon,
  ChevronUpIcon,
  DacrMarkIcon,
} from '@dacr/icons'

import Spinner from '@/shared/components/Spinner.vue'
import { fileTypeToThumbnail } from '@/shared/utils/thumbnail'
import type { File, Item } from '@/shared/graphql/router/graphql'
import { useSelectCopyright } from '../useSelectCopyright'

const { close } = useSheet()
const {
  loading,
  loadMore,
  sort,
  items,
  breadcrumbPath,
  breadcrumbItems,
  thumbSrc,
  timestampLabel,
  moreCount,
  handleBreadcrumbSelect,
  openFolder,
  selected,
  confirm,
  isFile,
  isFolder,
} = useSelectCopyright()

const handleScroll = (e: Event) => {
  const el = e.target as HTMLElement
  if (el.scrollHeight - el.scrollTop - el.clientHeight < 100) loadMore()
}

const breadcrumbKey = computed(() => breadcrumbPath.value.join('-'))

const fileRowIds = computed(() =>
  items.value.filter((item) => isFile(item)).map((item) => item.id)
)

const selectedIds = computed<string[]>({
  get: () => (selected.value ? [selected.value.id] : []),
  set: (ids) => {
    const id = ids[0]
    selected.value = id
      ? items.value.find((item): item is File => isFile(item) && item.id === id)
      : undefined
  },
})

const handleRowClick = (item: Item) => {
  if (isFolder(item)) return openFolder(item)
}
</script>
